import { strapi } from '@strapi/client';
import { put } from 'redux-saga/effects';
import * as zod from 'zod';

import { getScribeTemplatesByCategorySuccess, getScribeTemplatesFailed } from 'js/actionCreators/templatesActions';

// https://github.com/colinhacks/zod/issues/4461
zod.config({ jitless: true });

const AssetSchema = zod.object({
  documentId: zod.string(),
  url: zod.url()
});

const ProjectTemplateSchema = zod.object({
  documentId: zod.string(),
  title: zod.string(),
  canvasShape: zod.enum(['landscape', 'portrait', 'square']),
  premium: zod.boolean(),
  sparkolTemplateId: zod.string(),
  thumbnail: AssetSchema,
  previewVideo: AssetSchema.nullable(),
  publishedAt: zod.coerce.date()
});

const TemplatesCategorySchema = zod.object({
  documentId: zod.string(),
  title: zod.string(),
  project_templates: zod.array(ProjectTemplateSchema)
});

const TemplatesListSchema = zod.object({
  template_categories: zod.array(TemplatesCategorySchema)
});

export function* getTemplatesSaga(): Generator<unknown, void, { data: zod.infer<typeof TemplatesListSchema> }> {
  const baseURL = import.meta.env.VITE_TEMPLATES_API_BASE_URL;

  if (!baseURL) {
    return console.error('No base URL set for templates API');
  }

  const accessToken = import.meta.env.VITE_TEMPLATES_API_ACCESS_TOKEN;

  if (!accessToken) {
    return console.error('No access token set for templates API');
  }

  const client = strapi({
    baseURL,
    auth: accessToken
  });

  try {
    const response = yield client
      .single('vsc-template-category-list')
      .find({
        populate: [
          'template_categories',
          'template_categories.project_templates',
          'template_categories.project_templates.thumbnail',
          'template_categories.project_templates.previewVideo'
        ]
      });
    const categories = TemplatesListSchema.parse(response.data).template_categories.map(category => {
      return {
        id: category.documentId,
        title: category.title,
        templates: category.project_templates.map(template => ({
          id: template.documentId,
          title: template.title,
          canvasSize: template.canvasShape,
          isPremium: template.premium,
          thumbnailImage: template.thumbnail.url,
          previewUrl: template.previewVideo?.url,
          sparkolTemplateId: template.sparkolTemplateId,
          isTemplate: true,
          createdDate: template.publishedAt
        }))
      };
    });

    yield put(getScribeTemplatesByCategorySuccess(categories));
  } catch (error) {
    if (error instanceof zod.ZodError) {
      console.error('Templates API Validation error:', error.issues);
    } else {
      console.error('Error fetching templates:', error);
    }
    yield put(getScribeTemplatesFailed(error as Error));
  }
}
